/**
 * Task Tracker — Visual To-Do Checklist for the Vexalyn Agent
 *
 * When the agent is asked to build a project, it generates a plan with tasks.
 * This module renders those tasks as a live terminal checklist and ticks them
 * off one-by-one (✅ green) as each completes.
 */

// ── ANSI helpers ───────────────────────────────────────────────────────────────
const RESET   = '\x1b[0m';
const BOLD    = '\x1b[1m';
const PURPLE  = '\x1b[38;2;168;85;247m';
const GREEN   = '\x1b[1;92m';
const GREY    = '\x1b[90m';
const YELLOW  = '\x1b[33m';
const WHITE   = '\x1b[97m';
const DIM     = '\x1b[2m';

const MIN_BOX_W = 44;
const MAX_BOX_W = 88;

function stripAnsi(text: string): string {
  return text.replace(/\x1b\[[0-9;]*m/g, '');
}

function visibleLength(text: string): number {
  return stripAnsi(text).length;
}

function terminalInnerWidth(): number {
  const cols = process.stdout.columns || 80;
  return Math.max(32, Math.min(MAX_BOX_W, cols - 4));
}

function wrapText(text: string, width: number): string[] {
  if (width <= 0) return [text];
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    if (word.length > width) {
      if (current) {
        lines.push(current);
        current = '';
      }
      for (let i = 0; i < word.length; i += width) {
        lines.push(word.slice(i, i + width));
      }
      continue;
    }

    const next = current ? `${current} ${word}` : word;
    if (next.length > width) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }

  if (current) lines.push(current);
  return lines;
}

function boxBorder(char: '╭' | '╰' | '├', width: number): string {
  const line = '─'.repeat(width + 2);
  const close = char === '╭' ? '╮' : char === '╰' ? '╯' : '┤';
  return `${PURPLE}${char}${line}${close}${RESET}`;
}

function boxLine(content: string, width: number): string {
  const pad = Math.max(0, width - visibleLength(content));
  return `${PURPLE}│${RESET} ${content}${' '.repeat(pad)} ${PURPLE}│${RESET}`;
}

// ── Task state ─────────────────────────────────────────────────────────────────
export interface Task {
  id: number;
  label: string;
  done: boolean;
}

export class TaskTracker {
  private tasks: Task[] = [];
  private title: string;
  private rendered = false;
  private renderedHeight = 0;
  private isLastOutput = false;

  constructor(title = 'Project Tasks') {
    this.title = title;
  }

  /** Add a list of task labels (clears any previous tasks). */
  setTasks(labels: string[]): void {
    this.tasks = labels.map((label, i) => ({ id: i + 1, label, done: false }));
    this.rendered = false;
    this.renderedHeight = 0;
    this.isLastOutput = false;
  }

  get count(): number { return this.tasks.length; }
  get doneCount(): number { return this.tasks.filter(t => t.done).length; }
  get remainingCount(): number { return this.tasks.filter(t => !t.done).length; }

  hasPending(): boolean {
    return this.remainingCount > 0;
  }

  /** Render the full task box for the first time. */
  render(): void {
    if (this.tasks.length === 0) return;
    const lines = this.buildLines();
    process.stdout.write('\n');
    for (const line of lines) process.stdout.write(line + '\n');
    this.rendered = true;
    this.renderedHeight = lines.length + 1;
    this.isLastOutput = true;
  }

  /** Mark that another UI block was printed after the task box. */
  markOutputInterrupted(): void {
    this.isLastOutput = false;
  }

  /**
   * Mark task at 0-based index as done and redraw the box in-place.
   * Redrawing the full box keeps wrapped rows aligned as labels change state.
   */
  complete(index: number): void {
    const task = this.tasks[index];
    if (!task || task.done) return;
    task.done = true;

    if (!this.rendered || !this.isLastOutput) {
      this.render();
      return;
    }

    this.redraw();
  }

  /** Complete the next pending task as a fallback when the agent forgets complete_task. */
  completeNextPending(): void {
    const index = this.tasks.findIndex((task) => !task.done);
    if (index >= 0) this.complete(index);
  }

  /** Complete any remaining visible tasks at the end of a successful agent turn. */
  completeRemaining(): void {
    if (this.tasks.length === 0) return;
    if (!this.rendered) {
      for (const task of this.tasks) task.done = true;
      this.render();
      return;
    }
    for (const task of this.tasks) {
      if (!task.done) this.complete(task.id - 1);
    }
  }

  /** Print a final "all done" summary below the task box. */
  summarize(): void {
    if (this.tasks.length === 0) return;
    const all = this.tasks.every(t => t.done);
    process.stdout.write('\n');
    if (all) {
      process.stdout.write(
        `${GREEN}✔ Semua ${this.count} task selesai!${RESET}\n`
      );
    } else {
      process.stdout.write(
        `${YELLOW}⚠ ${this.remainingCount} task belum selesai.${RESET}\n`
      );
    }
    process.stdout.write('\n');
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  private _rowText(task: Task): string {
    const num    = `${DIM}${String(task.id).padStart(2)}.${RESET}`;
    const check  = task.done
      ? `${GREEN}✔${RESET}`
      : `${GREY}○${RESET}`;
    const label  = task.done
      ? `${DIM}${task.label}${RESET}`
      : `${WHITE}${task.label}${RESET}`;
    return `${num} ${check} ${label}`;
  }

  private calculateWidth(): number {
    const maxInner = terminalInnerWidth();
    const titleLength = visibleLength(`◆ ${this.title}  (${this.doneCount}/${this.count})`);
    const rowLengths = this.tasks.map((task) => visibleLength(`  ${task.id}. ○ ${task.label}`));
    return Math.min(maxInner, Math.max(MIN_BOX_W, titleLength, ...rowLengths));
  }

  private buildTaskLines(task: Task, width: number): string[] {
    const num = `${DIM}${String(task.id).padStart(2)}.${RESET}`;
    const check = task.done ? `${GREEN}✔${RESET}` : `${GREY}○${RESET}`;
    const color = task.done ? DIM : WHITE;
    const prefix = `${num} ${check} `;
    const continuation = ' '.repeat(visibleLength(prefix));
    const labelWidth = Math.max(8, width - visibleLength(prefix));
    const wrapped = wrapText(task.label, labelWidth);

    return wrapped.map((line, index) => {
      const linePrefix = index === 0 ? prefix : continuation;
      return boxLine(`${linePrefix}${color}${line}${RESET}`, width);
    });
  }

  private buildLines(): string[] {
    const width = this.calculateWidth();
    const lines = [
      boxBorder('╭', width),
      boxLine(`${BOLD}${PURPLE}◆ ${this.title}${RESET}  ${GREY}(${this.doneCount}/${this.count})${RESET}`, width),
      boxBorder('├', width),
    ];

    for (const task of this.tasks) {
      lines.push(...this.buildTaskLines(task, width));
    }

    lines.push(boxBorder('╰', width));
    return lines;
  }

  private redraw(): void {
    if (!this.rendered || this.renderedHeight === 0) {
      this.render();
      return;
    }

    process.stdout.write(`\x1b[${this.renderedHeight}A`);
    for (let i = 0; i < this.renderedHeight; i++) {
      process.stdout.write('\r\x1b[2K');
      if (i < this.renderedHeight - 1) process.stdout.write('\n');
    }
    process.stdout.write(`\x1b[${this.renderedHeight - 1}A\r`);

    const lines = this.buildLines();
    process.stdout.write('\n');
    for (const line of lines) process.stdout.write(line + '\n');
    this.renderedHeight = lines.length + 1;
    this.isLastOutput = true;
  }
}

// ── Plan parser ────────────────────────────────────────────────────────────────

/**
 * Parse a numbered or bulleted task list from the agent's plan text.
 *
 * Accepts:
 *   1. Create folder structure
 *   2. Write index.html
 *   - Add CSS
 *   • Add JS
 *   **Step 1**: Setup project
 */
export function parseTasksFromPlan(planText: string): string[] {
  const lines = planText.split('\n');
  const tasks: string[] = [];

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Numbered: "1." "1)" "Step 1:"
    const numbered = line.match(/^(?:step\s*)?\d+[.):\s]\s*(.+)/i);
    if (numbered?.[1]) { tasks.push(numbered[1].trim()); continue; }

    // Bulleted: "- " "* " "• "
    const bulleted = line.match(/^[-*•]\s+(.+)/);
    if (bulleted?.[1]) { tasks.push(bulleted[1].trim()); continue; }

    // Bold step header: "**Create something**" or "**Step X: ...**"
    const bold = line.match(/^\*{1,2}(.+?)\*{1,2}[:\s]*(.*)$/);
    if (bold) {
      const label = (bold[2]?.trim() || bold[1]?.trim() || '').replace(/\*+/g, '').trim();
      if (label) { tasks.push(label); continue; }
    }
  }

  // Deduplicate while preserving order
  const seen = new Set<string>();
  return tasks.filter(t => {
    const k = t.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// ── Project detection ──────────────────────────────────────────────────────────

/** Keywords that strongly suggest a "build a project from scratch" request. */
const PROJECT_KEYWORDS = [
  'buat', 'buat project', 'buat website', 'buat app', 'buat aplikasi',
  'bikin', 'bikin project', 'bikin website', 'bikin app',
  'create', 'build', 'generate', 'scaffold', 'setup project',
  'initialize project', 'init project', 'make a',
];

export function isProjectRequest(message: string): boolean {
  const lower = message.toLowerCase();
  return PROJECT_KEYWORDS.some(kw => lower.includes(kw));
}

// ── Custom Tools for Agent Checklist ───────────────────────────────────────────
import { z } from 'zod';
import type { AgentTool } from '@vexalyn/tools';

export const globalTaskTracker = new TaskTracker();

export const createTasksTool: AgentTool<{ title: string; tasks: string[] }, { ok: boolean }> = {
  name: 'create_tasks',
  description: 'Initialize a project task checklist to track to-do items. Call this after a plan is approved and before writing any files/executing code.',
  inputSchema: z.object({
    title: z.string().describe('Title of the checklist (e.g. "Portfolio Setup").'),
    tasks: z.array(z.string()).describe('An ordered list of task labels/descriptions.'),
  }),
  async execute(input) {
    globalTaskTracker.setTasks(input.tasks);
    globalTaskTracker.render();
    return {
      ok: true,
      value: { ok: true },
    };
  },
};

export const completeTaskTool: AgentTool<{ id: number }, { ok: boolean }> = {
  name: 'complete_task',
  description: 'Mark a task from the checklist as completed (turns green ✅). Call this immediately after completing the work for a specific task ID.',
  inputSchema: z.object({
    id: z.number().describe('The 1-based ID of the task to complete (e.g., 1, 2, ...).'),
  }),
  async execute(input) {
    const index = input.id - 1;
    globalTaskTracker.complete(index);
    return {
      ok: true,
      value: { ok: true },
    };
  },
};
